import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Typography,
  Stack,
  Chip,
  LinearProgress,
  Card,
  CardContent,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import TaskService from '../../services/task.service';

const TaskList: React.FC = () => {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTasks();
  }, []);

  const fetchTasks = async () => {
    try {
      setLoading(true);
      const response = await TaskService.getAll({
        populate: '*',
        'pagination[pageSize]': 100,
      });
      setTasks(response.data?.data || []);
    } catch (error) {
      console.error('Error fetching tasks:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this task?')) {
      try {
        await TaskService.delete(id);
        fetchTasks();
      } catch (error) {
        console.error('Error deleting task:', error);
      }
    }
  };

  const getStatusColor = (status: string) => {
    const colors: any = {
      todo: 'default',
      inProgress: 'info',
      review: 'warning',
      done: 'success',
      blocked: 'error',
    };
    return colors[status] || 'default';
  };

  const getPriorityColor = (priority: string) => {
    const colors: any = {
      low: 'default',
      medium: 'info',
      high: 'warning',
      urgent: 'error',
    };
    return colors[priority] || 'default';
  };

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Tasks</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/tasks/create')}
        >
          New Task
        </Button>
      </Stack>

      {loading ? (
        <Typography>Loading...</Typography>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Title</TableCell>
                <TableCell>Project</TableCell>
                <TableCell>Assigned To</TableCell>
                <TableCell>Priority</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Progress</TableCell>
                <TableCell>Due Date</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tasks.map((task) => (
                <TableRow key={task.id} hover>
                  <TableCell>{task.attributes?.title}</TableCell>
                  <TableCell>
                    {task.attributes?.project?.data?.attributes?.projectName || '-'}
                  </TableCell>
                  <TableCell>
                    {task.attributes?.assignedTo?.data
                      ? `${task.attributes?.assignedTo?.data?.attributes?.firstName} ${task.attributes?.assignedTo?.data?.attributes?.lastName}`
                      : '-'}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={task.attributes?.priority}
                      size="small"
                      color={getPriorityColor(task.attributes?.priority)}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={task.attributes?.status}
                      size="small"
                      color={getStatusColor(task.attributes?.status)}
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LinearProgress
                        variant="determinate"
                        value={task.attributes?.progress || 0}
                        sx={{ width: 100 }}
                      />
                      <Typography variant="body2">
                        {task.attributes?.progress || 0}%
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    {task.attributes?.dueDate
                      ? new Date(task.attributes.dueDate).toLocaleDateString()
                      : '-'}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={() => navigate(`/tasks/${task.id}`)}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDelete(task.id)}
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {!loading && tasks.length === 0 && (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">
            No tasks found
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

export default TaskList;

